import { ActivityType } from 'interfaces/common.interface';
import { ONBOARD_FLOW_ROUTINE, SUGGESTED_HABITS_SECTION } from 'enums';
import { v4 as uuid } from 'uuid';
import { AccordionVariant } from 'components/ui/accordion';
import { ROUTINE } from './general';

export enum RoutineSuggestionsAsyncErrorCode {
  TASK_FAILED = 'TASK_FAILED',
  TASK_TIMED_OUT = 'TASK_TIMED_OUT'
}

export const ROUTINE_SUGGESTIONS_ASYNC_ERROR_I18N_KEY: Record<
  RoutineSuggestionsAsyncErrorCode,
  string
> = {
  [RoutineSuggestionsAsyncErrorCode.TASK_FAILED]:
    'routine_suggestion.async_task_failed',
  [RoutineSuggestionsAsyncErrorCode.TASK_TIMED_OUT]:
    'routine_suggestion.async_task_timed_out'
};

export enum ASYNC_TASK_STATUS {
  COMPLETED = 'completed',
  FAILED = 'failed',
  ERROR = 'error',
  PENDING = 'pending'
}

export const ROUTINE_SUGGESTIONS = {
  POLL_INITIAL_ATTEMPT: 0,
  POLL_MS: 1500,
  POLL_MAX_ATTEMPTS: 80
};

export const routineOptions = [
  {
    label: 'onboard_flow.morning',
    type: ONBOARD_FLOW_ROUTINE.MORNING_ROUTINE
  },
  {
    label: 'onboard_flow.evening',
    type: ONBOARD_FLOW_ROUTINE.EVENING_ROUTINE
  }
];

export const createDefaultBreakActivities = (): ActivityType[] => [
  {
    id: uuid(),
    name: 'Stretching',
    duration_seconds: 60,
    include_in_every_break: true,
    video_urls: ['https://www.youtube.com/watch?v=s5V_sVvagrM'],
    log_quantity: false,
    log_quantity_questions: [],
    allowed_apps: [],
    allowed_urls: [],
    is_office_friendly: true
  },
  {
    id: uuid(),
    name: 'Drink Water',
    duration_seconds: 30,
    include_in_every_break: true,
    video_urls: [],
    log_quantity: false,
    log_quantity_questions: [],
    allowed_apps: [],
    allowed_urls: [],
    is_office_friendly: true
  }
];

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export const REVAMP_HABIT_LIST_MIN_INSERT_INDEX = 0;

export const REVAMP_STEP_PROGRESS_MIN_INDEX = 0;

export const ROUTINE_CONFIG: Record<
  string,
  { variant: AccordionVariant; titleKey: string }
> = {
  [ROUTINE.MORNING]: { variant: 'morning', titleKey: 'morning_routine' },
  [ROUTINE.EVENING]: { variant: 'evening', titleKey: 'evening_routine' },
  [ROUTINE.BREAKING]: { variant: 'afternoon', titleKey: 'break_activities' }
};

export const DND_ACTIVATION_DISTANCE = 5;

export const ROUTINE_DROPPABLE_ID = {
  MORNING: 'morning-droppable'
} as const;

export const DND_ACTIVITY_CLASS_NAME = 'habit-drag-active';

export const DND_OPACITY = {
  NOT_DRAGGING: 1,
  DRAGGING: 0.4
};

export const ROUTINE_SUGGESTIONS_IDS = [
  ROUTINE.MORNING,
  ROUTINE.EVENING,
  ROUTINE.BREAKING
];

export const ROUTINE_HABITS_SKELETON_COUNTS: Record<string, number> = {
  [ROUTINE.MORNING]: 3,
  [ROUTINE.EVENING]: 2,
  [ROUTINE.BREAKING]: 2
};

export const ROUTINE_SUGGESTIONS_LOADING_VIDEO_URL =
  'https://www.youtube.com/shorts/fbNT2Ai21qo';

// Generation is normally much faster; the bar fills over this window so it feels
// steady rather than stalling if the request runs long.
export const ROUTINE_SUGGESTIONS_LOADING_PROGRESS_DURATION_MS = 40_000;

export const ROUTINE_GOAL_ID = {
  BOOST_PRODUCTIVITY: 'boost_productivity',
  IMPROVE_MENTAL_WELL_BEING: 'improve_mental_well_being',
  FOCUS_ON_HEALTH_AND_FITNESS: 'focus_on_health_and_fitness',
  MANAGE_HOME_TASKS: 'manage_home_tasks',
  STRENGTHEN_RELATIONSHIPS: 'strengthen_relationships'
} as const;

export const SUGGESTED_HABITS_SEARCH_THRESHOLD = 1;

export const SUGGESTED_HABITS_SECTION_TAG: Record<
  SUGGESTED_HABITS_SECTION,
  string
> = {
  [SUGGESTED_HABITS_SECTION.CHORES]: 'Manage home tasks (cleaning, chores)',
  [SUGGESTED_HABITS_SECTION.PRODUCTIVITY]:
    'Boost productivity (work, study, tasks)',
  [SUGGESTED_HABITS_SECTION.WELLBEING]:
    'Improve mental well-being (stress, sleep)',
  [SUGGESTED_HABITS_SECTION.HEALTH]:
    'Focus on health & fitness (exercise, nutrition)',
  [SUGGESTED_HABITS_SECTION.HEALTHY_HABITS]: 'Build healthy habits'
};

export const SUGGESTED_HABITS_SECTION_ORDER: SUGGESTED_HABITS_SECTION[] = [
  SUGGESTED_HABITS_SECTION.CHORES,
  SUGGESTED_HABITS_SECTION.PRODUCTIVITY,
  SUGGESTED_HABITS_SECTION.WELLBEING,
  SUGGESTED_HABITS_SECTION.HEALTH,
  SUGGESTED_HABITS_SECTION.HEALTHY_HABITS
];

export const SUGGESTED_HABITS_SECTION_I18N_KEY: Record<
  SUGGESTED_HABITS_SECTION,
  string
> = {
  [SUGGESTED_HABITS_SECTION.CHORES]:
    'routine_suggestion.suggested_habits_sections.chores',
  [SUGGESTED_HABITS_SECTION.PRODUCTIVITY]:
    'routine_suggestion.suggested_habits_sections.productivity',
  [SUGGESTED_HABITS_SECTION.WELLBEING]:
    'routine_suggestion.suggested_habits_sections.wellbeing',
  [SUGGESTED_HABITS_SECTION.HEALTH]:
    'routine_suggestion.suggested_habits_sections.health',
  [SUGGESTED_HABITS_SECTION.HEALTHY_HABITS]:
    'routine_suggestion.suggested_habits_sections.healthy_habits'
};

export const SUGGESTED_HABITS_TAG_TO_SECTION: Record<
  string,
  SUGGESTED_HABITS_SECTION
> = Object.fromEntries(
  SUGGESTED_HABITS_SECTION_ORDER.map((section) => [
    SUGGESTED_HABITS_SECTION_TAG[section],
    section
  ])
) as Record<string, SUGGESTED_HABITS_SECTION>;
